import { useEffect, useId, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@/ui/icons';
import { useVisualViewport } from '@/ui/layout/useVisualViewport';
import styles from './BottomSheet.module.css';
import { useOverlay } from './useOverlay';
import { EXIT } from '@/ui/motion/motion';

export interface BottomSheetProps {
  title: string;
  lead?: string;
  onClose: () => void;
  children: ReactNode;
  /** The confirm row, or a composer. Pinned under the content, above the home indicator. */
  footer?: ReactNode;
  /** Something small beside the title, such as a refresh button. */
  action?: ReactNode;
  /** Tall sheets (a conversation) take most of the screen; short ones fit their content. */
  tall?: boolean;
  /** The label of the close button in the head. */
  closeLabel: string;
  /**
   * No close button in the head, for a notice whose one button is its way out.
   * A swipe, the backdrop and Escape still close it. The request of
   * 5 October 2026 (third), section 1.
   */
  hideClose?: boolean;
  /**
   * Set while there is something that closing would throw away, such as a
   * photograph or a note not yet sent: every way of closing (the button, a
   * swipe, Escape, the backdrop) asks first, with these words. Request of
   * 4 October 2026, section 6b.
   */
  unsaved?: { question: string; keep: string; discard: string } | null;
}

/** How far down a sheet has to be pulled before letting go closes it. */
export const SWIPE_CLOSE_PX = 90;
/**
 * Or how fast: a flick closes it from a short pull, as a finger expects. In
 * pixels per millisecond, with the shortest pull a flick still needs.
 */
export const FLICK_VELOCITY = 0.5;
export const FLICK_MIN_PX = 24;
/** How long the closing slide runs before the sheet is gone: EXIT.normal. */
export const SHEET_EXIT_MS = EXIT.normal;

/**
 * A sheet that rises from the bottom of a phone and sits centred on a desk
 * screen. Request of 4 October 2026, sections 6, 6b and 7: the one sheet the
 * case movement, Lab Notes and proof of work all use.
 *
 * The head and the footer stay put and the content between them scrolls, so
 * the confirm is always on screen however long the content and however narrow
 * the phone. Pulled down by its head, it follows the finger and closes once let
 * go past SWIPE_CLOSE_PX; short of that it springs back. It sizes itself to the
 * visual viewport, so the on-screen keyboard pushes it up rather than covering
 * the field being typed in, and it keeps clear of the notch and the home
 * indicator.
 */
export function BottomSheet({
  title,
  lead,
  onClose,
  children,
  footer,
  action,
  tall = false,
  closeLabel,
  hideClose = false,
  unsaved = null,
}: BottomSheetProps) {
  const titleId = useId();
  const backdrop = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const start = useRef<number | null>(null);
  // In a ref as well as in state: the release reads it, and must not read the
  // value from a render that has not happened yet.
  const pulled = useRef(0);
  // The last two points of the drag, for its speed at the moment of letting go,
  // and how many moves there were: a flick is a run of them, never a single jump.
  const trail = useRef<{ y: number; t: number }[]>([]);
  const moves = useRef(0);
  const [drag, setDrag] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [asking, setAsking] = useState(false);

  const close = useRef(onClose);
  close.current = onClose;
  useOverlay();

  // Plays the slide out, then tells the owner. Once only.
  const leave = useRef(() => {});
  leave.current = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => close.current(), SHEET_EXIT_MS);
  };

  // Every way of closing comes through here, so unsaved work is asked about once, in one place.
  const request = useRef(() => {});
  request.current = () => {
    if (unsaved !== null && !asking) {
      setDrag(0);
      setAsking(true);
      return;
    }
    if (unsaved === null) leave.current();
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') request.current();
    };
    document.addEventListener('keydown', onKey);
    /*
     * Focus moves into the sheet: to the element marked `data-autofocus` when
     * there is one, wherever it sits, and otherwise to the first control, as it
     * always did. Asked in two steps, because one selector for both answers
     * with whichever comes first in the document, and that was a button in the
     * head, never the field the sheet was opened to fill. The request of
     * 5 October 2026 (third), section 2.
     */
    const sheet = box.current;
    (
      sheet?.querySelector<HTMLElement>('[data-autofocus]') ??
      sheet?.querySelector<HTMLElement>('input, textarea, button')
    )?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
   * The on-screen keyboard. It shrinks the visual viewport and not the layout
   * one, so a sheet fixed to the bottom of the layout viewport would sit behind
   * it. The backdrop follows the visual viewport instead, which lifts the sheet
   * above the keyboard, and the field being typed in is brought into view.
   */
  const keyboard = useVisualViewport(backdrop);

  function focused(event: React.FocusEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    /*
     * After the keyboard has had time to open and the viewport to shrink, the
     * field is brought into view by scrolling the sheet's own content and
     * nothing else. Request of 6 October 2026: scrollIntoView scrolls every box
     * above the field as well, the document included, and on an iPhone the page
     * behind was left shifted up once the keyboard closed.
     */
    setTimeout(() => {
      const content = target.closest<HTMLElement>(`.${styles.content}`);
      if (content === null) return;
      const frame = content.getBoundingClientRect();
      const field = target.getBoundingClientRect();
      if (field.bottom > frame.bottom) content.scrollTop += field.bottom - frame.bottom + 8;
      else if (field.top < frame.top) content.scrollTop -= frame.top - field.top + 8;
    }, 320);
  }

  function down(event: PointerEvent<HTMLDivElement>) {
    // The head drags, but not from its buttons: a list inside must still scroll.
    if ((event.target as HTMLElement).closest('button, input, textarea, a') !== null) return;
    start.current = event.clientY;
    trail.current = [{ y: event.clientY, t: event.timeStamp }];
    moves.current = 0;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function move(event: PointerEvent<HTMLDivElement>) {
    if (start.current === null) return;
    pulled.current = Math.max(0, event.clientY - start.current);
    trail.current = [...trail.current.slice(-1), { y: event.clientY, t: event.timeStamp }];
    moves.current += 1;
    setDrag(pulled.current);
  }

  function up() {
    if (start.current === null) return;
    start.current = null;
    const distance = pulled.current;
    pulled.current = 0;
    const [from, to] = trail.current;
    const velocity =
      from !== undefined && to !== undefined && to.t > from.t ? (to.y - from.y) / (to.t - from.t) : 0;
    const flicked = moves.current >= 2 && velocity > FLICK_VELOCITY && distance > FLICK_MIN_PX;
    // Short of either, it springs back (the transition in the stylesheet).
    if (distance > SWIPE_CLOSE_PX || flicked) request.current();
    else setDrag(0);
  }

  /*
   * Drawn in the body, not where it was opened. Request of 6 October 2026: on
   * an iPhone the page's own scroller is a layer of its own, and a sheet inside
   * it sat under the tab bar, its buttons hidden behind it. In the body it is
   * above everything, as its z-index says. React still sees it where it was
   * written, so clicks and keys reach the same handlers as before.
   */
  return createPortal(
    <div
      ref={backdrop}
      className={`${styles.backdrop} ${leaving ? styles.leaving : ''} ${keyboard ? styles.keyboard : ''}`}
      // The dimming lifts as the sheet is pulled away, and returns if it springs back.
      style={drag > 0 ? { backgroundColor: `rgba(24, 27, 29, ${String(Math.max(0.08, 0.34 - drag / 900))})` } : undefined}
      onClick={() => request.current()}
    >
      <div
        ref={box}
        className={`${styles.sheet} ${tall ? styles.tall : ''} ${drag > 0 ? styles.dragging : ''}`}
        style={drag > 0 ? { transform: `translateY(${drag}px)` } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        onFocus={focused}
      >
        <div
          className={styles.grab}
          data-testid="sheet-grab"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        >
          <span className={styles.handle} aria-hidden="true" />
          <div className={styles.head}>
            <div className={styles.titles}>
              <h3 id={titleId}>{title}</h3>
              {lead !== undefined && <p>{lead}</p>}
            </div>
            {action}
            {!hideClose && (
              <button
                type="button"
                className={styles.close}
                aria-label={closeLabel}
                onClick={() => request.current()}
              >
                <CloseIcon size={18} />
              </button>
            )}
          </div>
        </div>
        <div className={styles.content}>{children}</div>
        {asking && unsaved !== null ? (
          <div className={styles.foot} role="alertdialog" aria-label={unsaved.question}>
            <p className={styles.question}>{unsaved.question}</p>
            <div className={styles.actions}>
              <button type="button" className={styles.keep} onClick={() => setAsking(false)} data-autofocus>
                {unsaved.keep}
              </button>
              <button type="button" className={styles.discard} onClick={() => leave.current()}>
                {unsaved.discard}
              </button>
            </div>
          </div>
        ) : (
          footer !== undefined && <div className={styles.foot}>{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

